import { styled } from '@mui/material/styles';
import { Box, Typography, Paper, Container, Button, Chip, Avatar, Card, CardContent, Grid, IconButton } from '@mui/material';

// Common styled components to replace sx props
export const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
}));

export const StyledMainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  paddingTop: theme.spacing(10),
  display: 'flex',
  flexDirection: 'column',
}));

export const StyledContainer = styled(Container)(({ theme }) => ({
  maxWidth: 'lg',
}));

export const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.palette.mode === 'light' 
    ? '0 4px 12px rgba(0,0,0,0.05)' 
    : '0 4px 12px rgba(0,0,0,0.2)',
}));

export const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.palette.mode === 'light' 
    ? '0 4px 12px rgba(0,0,0,0.05)' 
    : '0 4px 12px rgba(0,0,0,0.2)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

export const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
}));

export const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: '8px 24px',
  boxShadow: 'none',
  fontWeight: 600,
  textTransform: 'none',
  letterSpacing: '0.02em',
  '&:hover': {
    boxShadow: 'none',
  },
}));

export const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: 16,
}));

export const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
}));

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 8,
}));

export const StyledTypography = styled(Typography)(({ theme }) => ({
  fontFamily: "'Nunito', 'Arial', sans-serif",
}));

export const StyledGrid = styled(Grid)(({ theme }) => ({
  // Grid specific styles if needed
}));

// Section containers
export const StyledSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.mode === 'dark' ? 'background.default' : '#f8f4f7',
}));

export const StyledSectionTitle = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

export const StyledSectionSubtitle = styled(Typography)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
}));

// Hero section specific
export const StyledHeroContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  color: 'white',
  textAlign: 'center',
  padding: theme.spacing(4),
}));

export const StyledHeroContent = styled(Box)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  marginBottom: theme.spacing(4),
}));

export const StyledHeroText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  lineHeight: 1.8,
  fontSize: '1.1rem',
  opacity: 0.9,
  textAlign: 'center',
}));

export const StyledHeroTagline = styled(Typography)(({ theme }) => ({
  lineHeight: 1.8,
  fontSize: '1.2rem',
  fontWeight: 'bold',
  opacity: 0.95,
  textAlign: 'center',
  color: theme.palette.primary.main,
}));

// Carousel specific
export const StyledCarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  maxWidth: '900px',
  margin: '0 auto',
}));

export const StyledCarouselPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 3,
  overflow: 'hidden',
  backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
  position: 'relative',
}));

export const StyledCarouselMedia = styled('img')(({ theme }) => ({
  height: 500,
  width: '100%',
  objectFit: 'contain',
  backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5',
  transition: 'transform 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.02)',
    filter: 'brightness(1.1)',
  },
}));

// Location section specific
export const StyledLocationMap = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  background: 'linear-gradient(135deg, #D7E4C0 0%, #B6C99A 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  borderRadius: 1,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3,
  },
}));

export const StyledLocationCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: 3,
  padding: theme.spacing(4),
  textAlign: 'center',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
}));

// Skip link for accessibility
export const StyledSkipLink = styled('a')(({ theme }) => ({
  position: 'absolute',
  left: '-9999px',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  textDecoration: 'none',
  color: 'inherit',
  '&:focus': {
    position: 'fixed',
    top: '10px',
    left: '10px',
    width: 'auto',
    height: 'auto',
    padding: '10px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    zIndex: 2000,
    textDecoration: 'none',
    fontWeight: 'bold',
    borderRadius: '4px',
    boxShadow: theme.shadows[3],
  },
})); 